import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import Snackbar from '../../Snackbar/Snackbar';
import useDevices from '../../../hooks/useDevices/useDevices';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import { AppError, isAppError } from '../../../utils/errors';

export function getSnackbarContent(hasAudio: boolean, hasVideo: boolean, error?: Error | AppError) {
  let headline = '';
  let message = '';

  switch (true) {
    // Handle AbortError typically caused by timeout starting video source
    case error?.name === 'AbortError':
      headline = 'Camera Connection Timeout:';
      message =
        'The camera took too long to start. Try refreshing the page, checking your camera connection, or testing your camera in another browser tab first.';
      break;

    // These custom errors are thrown by the useLocalTracks hook. They are thrown when the user explicitly denies
    // permission to only their camera, or only their microphone.
    case error?.message === 'CameraPermissionsDenied':
      headline = 'Unable to Access Media:';
      message =
        'The user has denied permission to use video. Please grant permission to the browser to access the camera.';
      break;
    case error?.message === 'MicrophonePermissionsDenied':
      headline = 'Unable to Access Media:';
      message =
        'The user has denied permission to use audio. Please grant permission to the browser to access the microphone.';
      break;

    // This error is emitted when the user or the user's system has denied permission to use the media devices
    case error?.name === 'NotAllowedError':
      headline = 'Unable to Access Media:';

      if (error!.message === 'Permission denied by system') {
        // Chrome only
        message =
          'The operating system has blocked the browser from accessing the microphone or camera. Please check your operating system settings.';
      } else {
        message =
          'The user has denied permission to use audio and video. Please grant permission to the browser to access the microphone and camera.';
      }

      break;

    // This error is emitted when input devices are not connected or disabled in the OS settings
    case error?.name === 'NotFoundError':
      headline = 'Cannot Find Microphone or Camera:';
      message =
        'The browser cannot access the microphone or camera. Please make sure all input devices are connected and enabled.';
      break;

    // Handle FailedToAcquireTracksError from useLocalTracks
    case error?.name === 'FailedToAcquireTracksError':
      headline = 'Unable to Initialize Media:';
      message =
        error!.message || 'Could not acquire audio or video tracks. Please check your device settings and try again.';
      break;

    // Other getUserMedia errors are less likely to happen in this app. Here we will display
    // the system's error message directly to the user.
    case Boolean(error):
      headline = 'Error Acquiring Media:';
      message = `${error!.name} ${error!.message}`;
      break;

    case !hasAudio && !hasVideo:
      headline = 'No Camera or Microphone Detected:';
      message = 'Other participants in the room will be unable to see and hear you.';
      break;

    case !hasVideo:
      headline = 'No Camera Detected:';
      message = 'Other participants in the room will be unable to see you.';
      break;

    case !hasAudio:
      headline = 'No Microphone Detected:';
      message = 'Other participants in the room will be unable to hear you.';
  }

  return {
    headline,
    message,
  };
}

export default function MediaErrorSnackbar({ error }: { error?: Error | AppError }) {
  const { hasAudioInputDevices, hasVideoInputDevices } = useDevices();

  const { isAcquiringLocalTracks } = useVideoContext();

  const [isSnackbarDismissed, setIsSnackbarDismissed] = useState(false);

  const errorCapturedRef = useRef(false);

  const isSnackbarOpen =
    !isSnackbarDismissed &&
    !isAcquiringLocalTracks &&
    (Boolean(error) || !hasAudioInputDevices || !hasVideoInputDevices);

  const { headline, message } = getSnackbarContent(hasAudioInputDevices, hasVideoInputDevices, error);

  // Reset capture ref when the error changes
  useEffect(() => {
    errorCapturedRef.current = false;
  }, [error]);

  // Capture error with Sentry
  useEffect(() => {
    if (error && isSnackbarOpen && !errorCapturedRef.current) {
      if (isAppError(error)) {
        errorCapturedRef.current = true;
        return;
      }

      // Filter out NotAllowedError as it's a user action, not an app error.
      // Chrome uses "Permission denied"; iOS Safari uses "not allowed by the user agent".
      if (error.name === 'NotAllowedError') {
        errorCapturedRef.current = true;
        return;
      }

      Sentry.withScope(scope => {
        scope.setContext('Media Error Snackbar', {
          hasAudioInputDevices,
          hasVideoInputDevices,
          isAcquiringLocalTracks,
          errorName: error.name,
          errorMessage: error,
          fullError: error,
        });
        Sentry.captureException(error);
      });

      errorCapturedRef.current = true;
    }
  }, [error, isSnackbarOpen, hasAudioInputDevices, hasVideoInputDevices, isAcquiringLocalTracks]);

  return (
    <Snackbar
      open={isSnackbarOpen}
      handleClose={() => setIsSnackbarDismissed(true)}
      headline={headline}
      message={message}
      variant="warning"
    />
  );
}
